import { Spatial } from "../../geo/Spatial";
/**
 * @class DirectionDOMCalculator
 * @classdesc Helper class for calculating DOM CSS properties.
 */
export class DirectionDOMCalculator {
    constructor(configuration, size) {
        this._minThresholdWidth = 320 * 0.2;
        this._maxThresholdWidth = 1480;
        this._minThresholdHeight = 240 * 0.2;
        this._maxThresholdHeight = 820;
        this._configure(configuration);
        this._resize(size);
        this._reset();
    }
    get minWidth() {
        return this._minWidth;
    }
    get maxWidth() {
        return this._maxWidth;
    }
    get containerWidth() {
        return this._containerWidth;
    }
    get containerWidthCss() {
        return this._containerWidthCss;
    }
    get containerMarginCss() {
        return this._containerMarginCss;
    }
    get containerLeftCss() {
        return this._containerLeftCss;
    }
    get containerHeight() {
        return this._containerHeight;
    }
    get containerHeightCss() {
        return this._containerHeightCss;
    }
    get containerBottomCss() {
        return this._containerBottomCss;
    }
    get stepCircleSize() {
        return this._stepCircleSize;
    }
    get stepCircleSizeCss() {
        return this._stepCircleSizeCss;
    }
    get stepCircleMarginCss() {
        return this._stepCircleMarginCss;
    }
    get turnCircleSize() {
        return this._turnCircleSize;
    }
    get turnCircleSizeCss() {
        return this._turnCircleSizeCss;
    }
    get outerRadius() {
        return this._outerRadius;
    }
    get innerRadius() {
        return this._innerRadius;
    }
    get shadowOffset() {
        return this._shadowOffset;
    }
    /**
     * Configures the min and max width values.
     *
     * @param {IDirectionConfiguration} configuration Configuration
     * with min and max width values.
     */
    configure(configuration) {
        this._configure(configuration);
        this._reset();
    }
    /**
     * Resizes all properties according to the width and height
     * of the size object.
     *
     * @param {ISize} size The size of the container element.
     */
    resize(size) {
        this._resize(size);
        this._reset();
    }
    /**
     * Calculates the coordinates on the unit circle for an angle.
     *
     * @param {number} angle Angle in radians.
     * @returns {Array<number>} The x and y coordinates on the unit circle.
     */
    angleToCoordinates(angle) {
        return [Math.cos(angle), Math.sin(angle)];
    }
    /**
     * Calculates the coordinates on the unit circle for the
     * relative angle between the first and second angle.
     *
     * @param {number} first Angle in radians.
     * @param {number} second Angle in radians.
     * @returns {Array<number>} The x and y coordinates on the unit circle
     * for the relative angle between the first and second angle.
     */
    relativeAngleToCoordinates(first, second) {
        const relativeAngle = Spatial.wrapAngle(first - second);
        return this.angleToCoordinates(relativeAngle);
    }
    _configure(configuration) {
        this._minWidth = configuration.minWidth;
        this._maxWidth = this._getMaxWidth(configuration.minWidth, configuration.maxWidth);
    }
    _resize(size) {
        this._elementWidth = size.width;
        this._elementHeight = size.height;
    }
    _reset() {
        this._containerWidth = this._getContainerWidth(this._elementWidth, this._elementHeight);
        this._containerHeight = this._getContainerHeight(this.containerWidth);
        this._stepCircleSize = this._getStepCircleDiameter(this._containerHeight);
        this._turnCircleSize = this._getTurnCircleDiameter(this.containerHeight);
        this._outerRadius = this._getOuterRadius(this._containerHeight);
        this._innerRadius = this._getInnerRadius(this._containerHeight);
        this._shadowOffset = 3;
        this._containerWidthCss = this._numberToCssPixels(this._containerWidth);
        this._containerMarginCss = this._numberToCssPixels(-0.5 * this._containerWidth);
        this._containerLeftCss = this._numberToCssPixels(Math.floor(0.5 * this._elementWidth));
        this._containerHeightCss = this._numberToCssPixels(this._containerHeight);
        this._containerBottomCss = this._numberToCssPixels(36); // Math.floor(-0.08 * this._containerHeight));
        this._stepCircleSizeCss = this._numberToCssPixels(this._stepCircleSize);
        this._stepCircleMarginCss = this._numberToCssPixels(-0.5 * this._stepCircleSize);
        this._turnCircleSizeCss = this._numberToCssPixels(this._turnCircleSize);
    }
    _getContainerWidth(elementWidth, elementHeight) {
        const relativeWidth = (elementWidth - this._minThresholdWidth) / (this._maxThresholdWidth - this._minThresholdWidth);
        const relativeHeight = (elementHeight - this._minThresholdHeight) / (this._maxThresholdHeight - this._minThresholdHeight);
        let coeff = Math.max(0, Math.min(1, Math.min(relativeWidth, relativeHeight)));
        coeff = 0.04 * Math.round(20 * coeff);
        return this._minWidth + coeff * (this._maxWidth - this._minWidth);
    }
    _getContainerHeight(containerWidth) {
        return 0.77 * containerWidth;
    }
    _getStepCircleDiameter(containerHeight) {
        return 0.3 * containerHeight;
    }
    _getTurnCircleDiameter(containerHeight) {
        return 0.3 * containerHeight;
    }
    _getOuterRadius(containerHeight) {
        return 0.25 * containerHeight;
    }
    _getInnerRadius(containerHeight) {
        return 0.125 * containerHeight;
    }
    _numberToCssPixels(value) {
        return value + "px";
    }
    _getMaxWidth(value, minWidth) {
        return value > minWidth ? value : minWidth;
    }
}
//# sourceMappingURL=DirectionDOMCalculator.js.map